import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, ListGroup, Spinner, Alert, Pagination } from 'react-bootstrap';
import { addTickerNote, editTickerNote, deleteTickerNote, togglePinNote } from '../services/api';

import { formatDateTime } from '../utils/dateUtils';

// Custom styles for pinned notes
const customStyles = `
  .note-item-pinned {
    background: linear-gradient(135deg, rgba(13, 110, 253, 0.16), rgba(13, 110, 253, 0.05)) !important;
    border-color: rgba(13, 110, 253, 0.45) !important;
    box-shadow: 0 0.35rem 0.75rem rgba(13, 110, 253, 0.18) !important;
    position: relative;
    overflow: hidden;
  }

  .note-item-pinned::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 1.5px solid rgba(13, 110, 253, 0.25);
    border-radius: inherit;
    pointer-events: none;
    opacity: 0.85;
  }

  [data-theme="dark"] .note-item-pinned {
    background: linear-gradient(135deg, rgba(46, 59, 92, 0.82), rgba(110, 168, 254, 0.28)) !important;
    border-color: rgba(110, 168, 254, 0.55) !important;
    box-shadow: 0 0.45rem 0.85rem rgba(13, 110, 253, 0.25) !important;
  }

  [data-theme="dark"] .note-item-pinned::after {
    border-color: rgba(110, 168, 254, 0.5);
    opacity: 0.9;
  }
`;

// formatDateTime function is now imported from dateUtils

const NotesModal = ({ show, onHide, ticker, notes, setNotesForTicker, floating = false, defaultY = null, defaultX = null }) => {
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  
  // Floating panel position/drag state
  const positionStorageKey = 'NotesModalPosition';
  const getDefaultPosition = () => {
    if (typeof window === 'undefined') {
      return { x: 600, y: 120 };
    }

    // Try to load saved position from localStorage
    try {
      const savedPosition = localStorage.getItem(positionStorageKey);
      if (savedPosition) {
        const parsed = JSON.parse(savedPosition);
        if (parsed && typeof parsed.x === 'number' && typeof parsed.y === 'number') {
          // Validate that saved position is still within viewport bounds
          const estimatedWidth = 420;
          const margin = 20;
          const maxX = Math.max(margin, window.innerWidth - estimatedWidth - margin);
          const maxY = Math.max(margin, window.innerHeight - 100);

          const x = Math.max(margin, Math.min(parsed.x, maxX));
          const y = Math.max(margin, Math.min(parsed.y, maxY));

          return { x, y };
        }
      }
    } catch (e) {
      console.error('Failed to load saved notes modal position:', e);
    }

    // Default position if no saved position exists
    const estimatedWidth = 420;
    const estimatedHeight = 520;
    const margin = 20;
    let x = defaultX != null ? defaultX : (window.innerWidth - estimatedWidth - margin); // align near right by default
    // Clamp X within viewport with margin
    x = Math.max(margin, Math.min(x, window.innerWidth - estimatedWidth - margin));
    let y = defaultY != null ? defaultY : Math.round((window.innerHeight - estimatedHeight) / 2);
    // Clamp to viewport with margin
    y = Math.max(margin, Math.min(y, Math.max(margin, window.innerHeight - estimatedHeight - margin)));
    return { x, y };
  };
  const [position, setPosition] = useState(getDefaultPosition());
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const notesPerPage = 3;
  
  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Calculate pagination values
  const totalNotes = notes ? notes.length : 0;
  const totalPages = Math.ceil(totalNotes / notesPerPage);
  const startIndex = (currentPage - 1) * notesPerPage;
  const endIndex = startIndex + notesPerPage;
  const currentNotes = notes ? notes.slice(startIndex, endIndex) : [];
  
  // Reset to first page when notes change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [notes]);

  // Persist position whenever it changes
  useEffect(() => {
    if (!floating) return;
    try {
      localStorage.setItem(positionStorageKey, JSON.stringify(position));
    } catch (_) {}
  }, [position, floating]);

  // Drag handlers for floating panel
  const handleDragStart = (e) => {
    if (!floating) return;
    setIsDragging(true);
    setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMouseMove = (e) => {
      const viewportWidth = window.innerWidth || 0;
      const viewportHeight = window.innerHeight || 0;
      const estimatedWidth = 420;
      let newX = e.clientX - dragOffset.x;
      let newY = e.clientY - dragOffset.y;
      // Constrain within viewport
      newX = Math.min(Math.max(0, newX), Math.max(0, viewportWidth - estimatedWidth));
      newY = Math.min(Math.max(0, newY), Math.max(0, viewportHeight - 100));
      setPosition({ x: newX, y: newY });
    };
    const onMouseUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await addTickerNote(ticker.ticker_id, newNote);
      // Insert new note and re-sort: pinned notes first, then by updated_at
      const updatedNotes = [res.data, ...(notes || [])];
      updatedNotes.sort((a, b) => {
        // First sort by pinned status (pinned notes first)
        if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
        // Then sort by updated_at (most recent first)
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
      setNotesForTicker(updatedNotes);
      setNewNote('');
      setShowAddNoteModal(false);
    } catch (e) {
      setError('Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  const openAddNoteModal = () => {
    setNewNote('');
    setError('');
    setShowAddNoteModal(true);
  };

  const closeAddNoteModal = () => {
    setNewNote('');
    setError('');
    setShowAddNoteModal(false);
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingText(note.note);
  };

  const handleSaveEdit = async () => {
    if (!editingText.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await editTickerNote(editingNoteId, editingText);
      // Update the note and re-sort: pinned notes first, then by updated_at
      const updatedNotes = notes.map(n => n.id === editingNoteId ? res.data : n);
      updatedNotes.sort((a, b) => {
        // First sort by pinned status (pinned notes first)
        if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
        // Then sort by updated_at (most recent first)
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
      setNotesForTicker(updatedNotes);
      setEditingNoteId(null);
      setEditingText('');
    } catch (e) {
      setError('Failed to update note');
    } finally {
      setLoading(false);
    }
  };

  // Delete handlers
  const openDeleteModal = (note) => {
    setNoteToDelete(note);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setNoteToDelete(null);
    setShowDeleteModal(false);
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    setDeleteLoading(true);
    setError('');

    try {
      await deleteTickerNote(noteToDelete.id);
      setNotesForTicker(notes.filter(n => n.id !== noteToDelete.id));
      closeDeleteModal();
    } catch (e) {
      setError('Failed to delete note');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Pin/Unpin handler
  const handleTogglePin = async (note) => {
    setLoading(true);
    setError('');
    try {
      const res = await togglePinNote(note.id);
      // Update the note and re-sort: pinned notes first, then by updated_at
      const updatedNotes = notes.map(n => n.id === note.id ? res.data : n);
      updatedNotes.sort((a, b) => {
        // First sort by pinned status (pinned notes first)
        if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
        // Then sort by updated_at (most recent first)
        return new Date(b.updated_at) - new Date(a.updated_at);
      });
      setNotesForTicker(updatedNotes);
    } catch (e) {
      setError('Failed to toggle pin');
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Render pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const items = [];
    const maxItems = 5; // Maximum number of page items to show
    
    // Add first page
    items.push(
      <Pagination.Item 
        key={1} 
        active={currentPage === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    );
    
    // Calculate start and end pages
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);
    
    // Adjust for edge cases
    if (currentPage <= 2) {
      endPage = Math.min(totalPages - 1, maxItems - 2);
    } else if (currentPage >= totalPages - 1) {
      startPage = Math.max(2, totalPages - maxItems + 2);
    }
    
    // Add ellipsis after first page if needed
    if (startPage > 2) {
      items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
    }
    
    // Add intermediate pages
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={currentPage === i}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    
    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
    }
    
    // Add last page if there are more than 1 page
    if (totalPages > 1) {
      items.push(
        <Pagination.Item 
          key={totalPages} 
          active={currentPage === totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted small">
          {totalNotes > 0 ? (
            <>
              Showing {startIndex + 1} to {Math.min(endIndex, totalNotes)} of {totalNotes} notes
            </>
          ) : (
            'No notes yet'
          )}
        </div>
        
        <Pagination size="sm">
          <Pagination.First 
            onClick={() => handlePageChange(1)} 
            disabled={currentPage === 1}
          />
          <Pagination.Prev 
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))} 
            disabled={currentPage === 1}
          />
          
          {items}
          
          <Pagination.Next 
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} 
            disabled={currentPage === totalPages}
          />
          <Pagination.Last 
            onClick={() => handlePageChange(totalPages)} 
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    );
  };

  const renderBodyContent = () => (
    <>
      <style>{customStyles}</style>
      {error && <Alert variant="danger">{error}</Alert>}
      <ListGroup variant="flush">
        {currentNotes.length > 0 ? currentNotes.map(note => (
          <ListGroup.Item key={note.id} className={note.pinned ? 'note-item-pinned' : ''}>
            {editingNoteId === note.id ? (
              <>
                <Form.Control
                  as="textarea"
                  rows={Math.max(3, Math.ceil(editingText.length / 60) + editingText.split('\n').length)}
                  value={editingText}
                  onChange={e => setEditingText(e.target.value)}
                  disabled={loading}
                  style={{ minHeight: '80px', resize: 'vertical' }}
                  autoFocus
                />
                <div className="d-flex gap-2 mt-2">
                  <Button size="sm" variant="success" onClick={handleSaveEdit} disabled={loading || !editingText.trim()}>
                    {loading ? <Spinner size="sm" animation="border" /> : 'Save'}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => { setEditingNoteId(null); setEditingText(''); }}>
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div style={{ whiteSpace: 'pre-wrap' }}>{note.note}</div>
                <div className="d-flex justify-content-between align-items-end mt-1">
                  <div className="text-muted small">{formatDateTime(note.updated_at || note.created_at)}</div>
                  <div className="d-flex gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="py-0 px-1"
                      title={note.pinned ? "Unpin" : "Pin to top"}
                      aria-label={note.pinned ? "Unpin" : "Pin"}
                      onClick={() => handleTogglePin(note)}
                    >
                      <i className={note.pinned ? "bi bi-pin-fill" : "bi bi-pin"} style={{ color: note.pinned ? '#0d6efd' : undefined }}></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="py-0 px-1"
                      title="Edit"
                      aria-label="Edit"
                      onClick={() => handleEditNote(note)}
                    >
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      className="py-0 px-1"
                      title="Delete"
                      aria-label="Delete"
                      onClick={() => openDeleteModal(note)}
                    >
                      <i className="bi bi-trash" style={{ color: '#dc3545' }}></i>
                    </Button>
                  </div>
                </div>
              </>
            )}
          </ListGroup.Item>
        )) : <div className="text-muted">No notes yet.</div>}
      </ListGroup>
      {renderPagination()}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button variant="primary" onClick={openAddNoteModal} size="sm">
          <i className="bi bi-plus-lg me-1"></i>
          Note
        </Button>
        <Button variant="secondary" onClick={onHide} size="sm">Close</Button>
      </div>
    </>
  );

  return (
    <>
      {!floating ? (
        <Modal show={show} onHide={onHide} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {ticker ? (
                <>
                  <strong>{ticker.symbol}</strong>
                  {ticker.name ? (
                    <span className="text-muted ms-2 small">{ticker.name}</span>
                  ) : null}
                </>
              ) : ''}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {renderBodyContent()}
          </Modal.Body>
        </Modal>
      ) : (
        show && (
          <div
            className="card shadow"
            style={{
              position: 'fixed',
              left: `${position.x}px`,
              top: `${position.y}px`,
              width: '420px',
              maxHeight: '75vh',
              overflow: 'hidden',
              zIndex: 1045
            }}
          >
            <div
              className="card-header d-flex justify-content-between align-items-center"
              onMouseDown={handleDragStart}
              style={{ cursor: 'move' }}
            >
              <div>
                {ticker ? (
                  <>
                    <strong>{ticker.symbol}</strong>
                    {ticker.name ? (
                      <span className="text-muted ms-2 small">{ticker.name}</span>
                    ) : null}
                  </>
                ) : ''}
              </div>
              <button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
            </div>
            <div className="card-body" style={{ overflowY: 'auto' }}>
              {renderBodyContent()}
            </div>
          </div>
        )
      )}

      {/* Add Note Modal */}
      <Modal show={showAddNoteModal} onHide={closeAddNoteModal} centered backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group>
            <Form.Label>Note</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              disabled={loading}
              placeholder="Enter your note..."
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAddNoteModal} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAddNote}
            disabled={loading || !newNote.trim()}
          >
            {loading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Adding...
              </>
            ) : (
              'Add Note'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Note</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this note?</p>
          {noteToDelete && (
            <div className="border rounded p-3 bg-light">
              <div style={{ whiteSpace: 'pre-wrap' }}>{noteToDelete.note}</div>
              <div className="text-muted small mt-2">
                {formatDateTime(noteToDelete.updated_at || noteToDelete.created_at)}
              </div>
            </div>
          )}
          <p className="text-muted small mt-2">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteModal} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteNote}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete Note'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NotesModal; 
